// v2 giraffe — the full towering build: very long legs with dark hooves, a deep
// chest sloping down to lower hindquarters, a two-segment neck rising steeply
// with a dark mane ridge, a refined head with a pale tapering muzzle, flared
// ears and knob-tipped ossicones, a tasselled tail — and the signature patch
// markings tiling body and neck on both sides. Two coats: warm tan with
// chestnut patches and a paler cream form. Ported 1:1 from age-of-mcp's
// v2/giraffe.ts, using the library's shared `critter` (quadLegs) and `safari`
// (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { coat: "#e2bd72", chest: "#e8c67e", belly: "#f0dfae", leg: "#e3c27c",
    patch: "#a9742f", mane: "#7a5526", muzzle: "#ecd9a8", hoof: "#6e5638",
    knob: "#6a4a26" },
  { coat: "#ecc98a", chest: "#f0d295", belly: "#f5e7c0", leg: "#ecca90",
    patch: "#b88440", mane: "#856032", muzzle: "#f0e0b6", hoof: "#78603e",
    knob: "#74542e" },
];

// Flattened patch markings [x, y, r] laid on both flanks (body) and the
// neck's sides — the giraffe's signature coat.
const BODY_PATCH: ReadonlyArray<readonly [number, number, number]> = [
  [0.04, 0.4, 0.027], [-0.04, 0.375, 0.025], [-0.105, 0.36, 0.023],
];
const NECK_PATCH: ReadonlyArray<readonly [number, number, number]> = [
  [0.135, 0.565, 0.018], [0.185, 0.69, 0.016],
];

function giraffeParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.085, dz: 0.048, bodyY: 0.37, rTop: 0.0145, rFoot: 0.0105,
    color: c.leg, hoof: { color: c.hoof, r: 0.0125, h: 0.018 },
  });
  parts.push(
    // deep chest sloping to lower hindquarters, pale belly
    { geometry: new THREE.IcosahedronGeometry(0.088, 0), color: c.chest,
      position: [0.05, 0.385, 0], scale: [1.15, 1.0, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.077, 0), color: c.coat,
      position: [-0.085, 0.355, 0], scale: [1.05, 0.95, 0.88] },
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.belly,
      position: [-0.01, 0.35, 0], scale: [1.45, 0.72, 0.86] },
    // the towering two-segment neck with a dark mane ridge behind it
    safari.tube([0.1, 0.45, 0], [0.175, 0.63, 0], 0.041, 0.031, c.coat, 6),
    safari.tube([0.175, 0.63, 0], [0.235, 0.79, 0], 0.031, 0.0235, c.coat, 6),
    { geometry: new THREE.BoxGeometry(0.016, 0.115, 0.011), color: c.mane,
      position: [0.121, 0.545, 0], rotation: [0, 0, -0.395] },
    { geometry: new THREE.BoxGeometry(0.014, 0.105, 0.01), color: c.mane,
      position: [0.189, 0.715, 0], rotation: [0, 0, -0.36] },
    // refined head: pale tapering muzzle, dark nose
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.coat,
      position: [0.272, 0.805, 0], scale: [1.35, 0.92, 0.85] },
    safari.tube([0.295, 0.8, 0], [0.345, 0.79, 0], 0.021, 0.0135, c.muzzle, 5),
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: c.knob,
      position: [0.35, 0.79, 0] },
    // tasselled tail
    safari.tube([-0.16, 0.35, 0], [-0.2, 0.2, 0.005], 0.006, 0.0035, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.01, 0), color: c.mane,
      position: [-0.205, 0.19, 0.006] },
  );
  for (const s of [1, -1] as const) {
    // flared ears + knob-tipped ossicones
    parts.push(
      { geometry: new THREE.ConeGeometry(0.0085, 0.026, 4), color: c.coat,
        position: [0.258, 0.83, s * 0.028], rotation: [s * 0.9, 0, 0.1] },
      { geometry: new THREE.CylinderGeometry(0.005, 0.005, 0.034, 4),
        color: c.coat, position: [0.268, 0.852, s * 0.0125] },
      { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.knob,
        position: [0.268, 0.871, s * 0.0125] },
    );
    // patch markings on this flank and this side of the neck
    for (const [px, py, pr] of BODY_PATCH) {
      parts.push({ geometry: new THREE.IcosahedronGeometry(pr, 0),
        color: c.patch, position: [px, py, s * 0.06],
        scale: [1.1, 1.0, 0.28] });
    }
    for (const [px, py, pr] of NECK_PATCH) {
      parts.push({ geometry: new THREE.IcosahedronGeometry(pr, 0),
        color: c.patch, position: [px, py, s * 0.025],
        scale: [1.0, 1.2, 0.3] });
    }
  }
  parts.push(...safari.eyes(0.292, 0.815, 0.0265, 0.005, "#2a2018"));
  return parts;
}

export const giraffeV2 = defineModel({
  id: "giraffe-v2",
  scale: 2.23, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Giraffe (v2)",
  tags: ["creature","giraffe","low-poly","age-of-mcp","fable"],
  summary: "Low-poly giraffe.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.2,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => giraffeParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => giraffeParts(1) },
  ],
  animations: [],
});
